// Dashboard export (Feature 2, P1). CSV downloads instantly via a Blob from the
// data already loaded into the dashboard. PDF is generated SERVER-SIDE (branded,
// a real file) and streamed down via GET /aai/dashboard/report.pdf.
import type { ChampionHomeData, OwnerHomeData } from '../../../types/aai';
import { aaiApi } from '../../../services/aaiApi';

function csvCell(v: unknown): string {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function download(name: string, mime: string, body: string): void {
  const blob = new Blob([body], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const today = () => new Date().toISOString().slice(0, 10);

export function exportChampionCsv(data: ChampionHomeData): void {
  const rows: string[][] = [];
  const companyPct = data.maturity?.suppressed ? 'suppressed' : `${data.maturity?.maturity_pct ?? ''}`;
  rows.push(['AI Adoption — company report', today()]);
  rows.push([]);
  rows.push(['Company maturity %', companyPct, `onboarded ${data.enrolled}`]);
  rows.push([]);
  rows.push(['Department', 'Maturity %', 'Band', 'Onboarded']);
  data.departments.forEach((d) =>
    rows.push([d.department, d.suppressed ? 'suppressed' : `${d.maturity_pct ?? ''}`, d.band ?? '', `${d.onboarded}`]),
  );
  rows.push([]);
  rows.push(['Use-case registry (approved)']);
  rows.push(['Use case', 'Department', 'Risk', 'Model', 'Human-in-loop', 'Status']);
  data.registry.forEach((g) =>
    rows.push([g.name, g.department, g.risk, g.model || '', g.human_in_loop || '', g.status]),
  );
  const csv = rows.map((r) => r.map(csvCell).join(',')).join('\n');
  download(`ai-adoption-${today()}.csv`, 'text/csv;charset=utf-8', csv);
}

export function exportOwnerCsv(data: OwnerHomeData): void {
  const rows: string[][] = [];
  rows.push(['AI Adoption — workspace report', today()]);
  rows.push([]);
  rows.push(['Members', `${data.stats.members}`, 'Seats', `${data.stats.seats}`]);
  rows.push(['Onboarded', `${data.stats.onboarded}`, 'Plan', data.stats.plan]);
  rows.push(['Company maturity %', data.stats.maturity_suppressed ? 'suppressed' : `${data.stats.maturity_pct ?? ''}`]);
  rows.push([]);
  rows.push(['Member', 'Email', 'Department', 'Workspace role', 'System role']);
  data.team.forEach((m) => rows.push([m.name, m.email, m.department, m.role, m.system]));
  const csv = rows.map((r) => r.map(csvCell).join(',')).join('\n');
  download(`ai-adoption-workspace-${today()}.csv`, 'text/csv;charset=utf-8', csv);
}

// Server-generated, branded adoption report PDF (a real file, not a browser
// print). Champion + owner share the same company report endpoint.
export async function downloadAdoptionReportPdf(): Promise<void> {
  const res = await aaiApi.downloadReportPdf();
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'ai-adoption-report.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
